import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL as API } from '../lib/api';
import { canOpenPage, FINANCE_PAGES } from '../lib/permissions';

const AuthContext = createContext(null);

// ── RBAC frontend (2e couche de défense — l'autorité reste le backend RBAC-1) ──
// Aligné sur backend/utils/rbac.py (chantier RBAC-1, IDOR P0-1 du 21/06) :
//   fondateur     = admin            → accès RH TOTAL  (isAdmin + isRH)
//   cercle élargi = manager          → accès RH TOTAL  (isRH)
//   standard      = readonly | user  → PAS de RH en masse, voit seulement SA fiche
// Le défaut historique 'user' (auth.py) + tout rôle inconnu/vide → 'readonly'
// (= standard, le MOINS privilégié, fail-closed). Cf rbac.normalize_role.
// ── 28/07 — LES SEULS RÔLES DU LOGICIEL (décision Théo) ──────────────────────
//   fondateur                  = admin        → tout, Finance et Administration comprises
//   responsable d'exploitation = exploitation → tout SAUF la Finance
//   chef d'équipe              = chef_equipe  → planning / terrain / sondages, lecture seule
//   agent                      = agent        → AUCUN accès logiciel (app mobile uniquement)
//
// `manager` et `readonly` sont des rôles HISTORIQUES supprimés : plus aucun compte ne
// les porte (inventaire prod du 28/07). Ils restent acceptés en entrée et repliés sur
// le rôle équivalent, pour qu'un vieux document en base ne verrouille personne dehors.
export const ROLES_LOGICIEL = ['admin', 'exploitation', 'chef_equipe'];
export const RH_ROLES = ['admin', 'exploitation'];   // accès aux données RH sensibles

/** Normalise un rôle DB → un des 4 rôles cibles. Un rôle inconnu tombe sur `agent`
 *  (aucun accès logiciel) : fail-closed volontaire. */
export function normalizeRole(role) {
  const r = (role || '').trim();
  if (r === 'admin' || r === 'exploitation' || r === 'chef_equipe' || r === 'agent') return r;
  if (r === 'manager') return 'exploitation';    // ancien « cercle élargi »
  if (r === 'readonly') return 'chef_equipe';    // ancien « standard lecture seule »
  return 'agent';
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/auth/me`, { withCredentials: true });
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    await axios.post(`${API}/api/auth/login`, { email, password }, { withCredentials: true });
    // A0 : /auth/login ne renvoie que {id,email,name,role} — on hydrate le user
    // complet (dont `permissions` résolues) via /auth/me pour que le menu et le
    // routing soient corrects DÈS le login (sans attendre un reload).
    const { data } = await axios.get(`${API}/api/auth/me`, { withCredentials: true });
    setUser(data);
    return data;
  };

  const register = async (email, password, name) => {
    const { data } = await axios.post(`${API}/api/auth/register`, { email, password, name }, { withCredentials: true });
    setUser(data);
    return data;
  };

  const logout = async () => {
    await axios.post(`${API}/api/auth/logout`, {}, { withCredentials: true });
    setUser(false);
  };

  // ── Helpers de rôle dérivés (consommés par RequireRole / nav / masquage PII) ──
  // `user` vaut : null (chargement), false (non authentifié), ou l'objet /auth/me
  // ({id, email, name, role}). `role` n'est dérivé que pour un user authentifié.
  const isAuthed = !!user && typeof user === 'object';
  const role = isAuthed ? normalizeRole(user.role) : null;
  const isAdmin = role === 'admin';                       // fondateur seul
  // `permissions` vient de /auth/me ({role, pages, data_tiers, can_write}) : c'est la
  // matrice résolue par le serveur, donc la SOURCE DE VÉRITÉ.
  const permissions = isAuthed ? (user.permissions || null) : null;
  // isRH = droit de voir les données RH sensibles (NIR, date de naissance, salaire).
  // 28/07 — Ce drapeau valait « admin ou manager » : le Responsable d'exploitation en
  // était exclu alors que le serveur lui accorde le tier `rh` depuis le 15/07. Résultat
  // concret : Armen se voyait refuser l'écran DPAE, et le NIR lui était masqué sur les
  // fiches — alors qu'une DPAE est impossible sans. On lit désormais le TIER renvoyé par
  // le serveur, avec repli sur le rôle si la matrice n'est pas encore chargée.
  const isRH = permissions?.data_tiers
    ? permissions.data_tiers.rh === true
    : (role === 'admin' || role === 'exploitation');

  /** True si le rôle courant ∈ `roles` (string ou array). */
  const hasRole = (roles) => {
    if (!role) return false;
    const list = Array.isArray(roles) ? roles : [roles];
    return list.includes(role);
  };

  /** True si l'utilisateur peut voir LA fiche `employeeId` (miroir exact de
   *  rbac.can_view_employee) : rôle RH OU propriétaire (user.employee_id ===
   *  employeeId). Fail-closed si employee_id absent côté user OU côté paramètre.
   *  ⚠️ Dépend de `user.employee_id` qui n'est PAS encore exposé par /auth/me
   *  (auth.py:64 ne renvoie que id/email/name/role). Tant que ce champ manque,
   *  le self-service côté front reste fail-closed (seuls les RH passent) — ce qui
   *  est SANS impact aujourd'hui (les 5 comptes prod sont admin). Voir note COORD. */
  const canViewEmployee = (employeeId) => {
    if (isRH) return true;
    const own = isAuthed ? user.employee_id : null;
    return !!own && !!employeeId && own === employeeId;
  };

  // ── A0 — accès par PAGE / TIER / écriture, lus depuis la matrice résolue ──────
  // `permissions` est déclaré plus haut (il sert aussi à dériver isRH).
  // Repli rollout-safe si absent (blip réseau / vieux cache) : un compte RH n'est
  // jamais verrouillé ; les autres retombent sur un socle minimal fail-closed.

  /** True si le compte peut OUVRIR `page` (menu + routing). */
  const canOpen = (page) => {
    if (!isAuthed) return false;
    if (permissions && Array.isArray(permissions.pages)) return canOpenPage(permissions, page);
    // Repli : permissions non chargées → admin/manager voient tout, sinon socle sûr.
    if (isRH) return true;
    return ['dashboard', 'inbox', 'settings'].includes(page);
  };

  /** True si le compte a le droit d'ÉCRIRE (POST/PUT/DELETE). Lecture seule sinon. */
  const canWrite = permissions ? permissions.can_write !== false : isRH;

  /** True si le tier de données sensibles (`rh`|`finance`|`strategy`) est ouvert. */
  const hasTier = (tier) => {
    if (!isAuthed) return false;
    if (permissions && permissions.data_tiers) return !!permissions.data_tiers[tier];
    return isRH; // repli : cercle élargi+ voit les données sensibles
  };

  return (
    <AuthContext.Provider value={{
      user, loading, login, register, logout,
      role, isAdmin, isRH, hasRole, canViewEmployee,
      permissions, canOpen, canWrite, hasTier,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
